/**
 * Email Health Widget — Minimal status indicator.
 * Just a green/red/amber dot. Click to refresh. No expanded details.
 */

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw } from 'lucide-react';
import { getAuthToken } from '../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailHealthWidget = ({ isDark = false }) => {
  const [overall, setOverall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Race guards: only the latest fetch's result is allowed to update state.
  // Without this, React 18 StrictMode dev double-mount + the 2 widgets we
  // render (sidebar + mobile) cause 4 concurrent fetches that race; if any
  // late-resolving promise hits an error first, the UI flashes "EMAIL DOWN"
  // even when the backend is fully healthy.
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);

  const fetchStatus = async (isRefresh = false) => {
    const reqId = ++requestIdRef.current;
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/email-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Drop the response if a newer fetch has started OR the component unmounted
      if (!mountedRef.current || reqId !== requestIdRef.current) return;
      const status = res?.data?.overall_status;
      // Only set 'error' when the backend explicitly returns it. If status is
      // missing for any reason, keep whatever we had before instead of flashing red.
      if (status) setOverall(status);
    } catch (e) {
      if (!mountedRef.current || reqId !== requestIdRef.current) return;
      setOverall('error');
    } finally {
      if (!mountedRef.current || reqId !== requestIdRef.current) return;
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    fetchStatus();
    return () => { mountedRef.current = false; };
  }, []);

  // Map status → dot color + label
  const map = {
    healthy:           { dot: 'bg-green-500',  ring: 'ring-green-500/40',  label: 'Email OK' },
    degraded:          { dot: 'bg-amber-500',  ring: 'ring-amber-500/40',  label: 'Email Issue' },
    broken:            { dot: 'bg-red-500',    ring: 'ring-red-500/40',    label: 'Email Down' },
    not_configured:    { dot: 'bg-gray-400',   ring: 'ring-gray-400/40',   label: 'Not Set Up' },
    domain_not_found:  { dot: 'bg-gray-400',   ring: 'ring-gray-400/40',   label: 'Not Set Up' },
    error:             { dot: 'bg-red-500',    ring: 'ring-red-500/40',    label: 'Email Down' },
  };
  const cfg = map[overall] || map.error;

  return (
    <div
      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg ${isDark ? 'bg-gray-800/60' : 'bg-gray-100'}`}
      data-testid="email-health-widget"
    >
      {loading ? (
        <Loader2 size={14} className={`animate-spin ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
      ) : (
        <span
          className={`relative w-2.5 h-2.5 rounded-full ${cfg.dot} ring-4 ${cfg.ring}`}
          data-testid="email-health-dot"
          data-status={overall || 'unknown'}
        >
          {(overall === 'healthy') && (
            <span className={`absolute inset-0 rounded-full ${cfg.dot} animate-ping opacity-60`} />
          )}
        </span>
      )}
      <span className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
        {loading ? 'Checking…' : cfg.label}
      </span>
      <button
        onClick={() => fetchStatus(true)}
        disabled={refreshing || loading}
        className={`ml-auto p-1 rounded transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-white text-gray-500'} disabled:opacity-40`}
        title="Refresh email status"
        data-testid="email-health-refresh"
      >
        <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
      </button>
    </div>
  );
};

export default EmailHealthWidget;
